import { useQuery } from "@tanstack/react-query";
import { CircleAlert, ExternalLink, FileText, LoaderCircle, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { api, type InboundInvoiceList } from "@/client";
import { MetricStrip } from "@/components/MetricStrip";
import { QiveArchiveCoverage } from "@/components/QiveArchiveCoverage";
import { currentMonth } from "@/domain/time";
import { FinancePresentation } from "@/presentation/finance";

type Invoice = InboundInvoiceList["documents"][number];

const statusLabels: Record<string, string> = {
	authorized: "Autorizada",
	canceled: "Cancelada",
};

export function InboundInvoicesView() {
	const month = currentMonth().id;
	const [search, setSearch] = useState("");
	const [status, setStatus] = useState<"ALL" | "authorized" | "canceled">("ALL");
	const query = useQuery({
		queryKey: ["qive-archive", "inbound-invoices", month],
		queryFn: () => api.qiveArchive.inboundInvoices({ month }),
	});
	const documents = useMemo(() => {
		if (!query.data) {
			return [];
		}

		const term = search.trim().toLocaleLowerCase("pt-BR");

		return query.data.documents.filter(
			(document) =>
				(status === "ALL" || document.status === status) &&
				(!term ||
					`${document.number} ${document.externalId} ${document.issuerName} ${document.issuerTaxId} ${document.description}`
						.toLocaleLowerCase("pt-BR")
						.includes(term)),
		);
	}, [query.data, search, status]);

	if (query.isPending) {
		return <Loading label="Abrindo as notas de entrada…" />;
	}

	if (query.error) {
		return <LoadError error={query.error} />;
	}

	const data = query.data;

	return (
		<div className="space-y-6">
			<MetricStrip
				items={[
					{
						label: "Notas de entrada",
						value: String(data.summary.count),
						detail: "destinadas à SINDI",
					},
					{
						label: "Valor documental",
						value: FinancePresentation.money(data.summary.totalCents),
						detail: "não significa obrigação",
					},
					{
						label: "Autorizadas",
						value: String(data.summary.authorizedCount),
						detail: data.month.label,
						tone: "success",
					},
					{
						label: "Canceladas",
						value: String(data.summary.canceledCount),
						detail: "preservadas no acervo",
						tone: data.summary.canceledCount > 0 ? "attention" : "neutral",
					},
				]}
			/>

			<QiveArchiveCoverage coverage={data.coverage} />

			<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
				<header className="grid gap-3 border-b border-stone-200 p-5 lg:grid-cols-[minmax(260px,1fr)_190px]">
					<label className="relative">
						<Search className="pointer-events-none absolute left-3 top-3 size-4 text-stone-400" />
						<input
							type="search"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Buscar emitente, CNPJ, número ou item"
							className="h-10 w-full rounded-xl border border-stone-200 pl-9 pr-3 text-sm outline-none focus:border-emerald-700"
						/>
					</label>
					<select
						value={status}
						onChange={(event) => {
							const value = event.target.value;
							setStatus(value === "authorized" || value === "canceled" ? value : "ALL");
						}}
						className="h-10 rounded-xl border border-stone-200 bg-white px-3 text-sm outline-none focus:border-emerald-700"
					>
						<option value="ALL">Todos os estados</option>
						<option value="authorized">Autorizadas</option>
						<option value="canceled">Canceladas</option>
					</select>
				</header>

				<div className="divide-y divide-stone-100">
					{documents.map((document) => (
						<InvoiceRow key={document.id} document={document} />
					))}
					{documents.length === 0 && (
						<p className="px-5 py-12 text-center text-sm text-stone-500">
							Nenhuma nota encontrada com estes filtros.
						</p>
					)}
				</div>
			</section>
		</div>
	);
}

function InvoiceRow({ document }: { document: Invoice }) {
	return (
		<article className="grid gap-4 px-5 py-5 lg:grid-cols-[110px_minmax(240px,1.2fr)_minmax(260px,1fr)_150px_auto] lg:items-center">
			<div>
				<p className="text-xs font-medium text-stone-500">
					{FinancePresentation.transactionDate(document.issuedOn)}
				</p>
				<p className="mt-1 text-sm font-semibold text-stone-950">NF-e {document.number}</p>
				<p className="mt-1 text-xs text-stone-400">
					{document.series ? `Série ${document.series}` : "Série não informada"}
				</p>
			</div>
			<div className="min-w-0">
				<p className="truncate text-sm font-semibold text-stone-900">{document.issuerName}</p>
				<p className="mt-1 text-xs text-stone-500">
					{FinancePresentation.taxId(document.issuerTaxId)}
				</p>
			</div>
			<div className="min-w-0">
				<p className="line-clamp-2 text-sm leading-5 text-stone-700">
					{document.description || "Sem descrição de itens disponível"}
				</p>
				<p className="mt-1 text-xs text-stone-400">
					{document.items.length} {document.items.length === 1 ? "item" : "itens"}
					{document.eventTypes.length > 0 &&
						` · ${document.eventTypes.length} evento${document.eventTypes.length === 1 ? "" : "s"}`}
				</p>
				<p
					className="mt-1 truncate font-mono text-[10px] text-stone-400"
					title={document.externalId}
				>
					Chave {document.externalId}
				</p>
			</div>
			<div className="lg:text-right">
				<p className="text-sm font-semibold text-stone-950">
					{FinancePresentation.money(document.totalCents)}
				</p>
				<span
					className={`mt-1 inline-flex rounded-full px-2 py-1 text-[11px] font-semibold ${
						document.status === "canceled"
							? "bg-amber-100 text-amber-800"
							: "bg-emerald-100 text-emerald-800"
					}`}
				>
					{statusLabels[document.status] ?? document.status}
				</span>
			</div>
			<a
				href={`/api/fiscal-document.pdf?id=${encodeURIComponent(document.id)}`}
				target="_blank"
				rel="noreferrer"
				className="inline-flex h-9 items-center justify-center gap-2 rounded-xl border border-stone-200 px-3 text-xs font-semibold text-stone-700 transition hover:border-emerald-700 hover:text-emerald-800"
			>
				<FileText className="size-3.5" />
				DANFE
				<ExternalLink className="size-3" />
			</a>
		</article>
	);
}

function Loading({ label }: { label: string }) {
	return (
		<div className="grid min-h-80 place-items-center text-sm font-medium text-stone-500">
			<span className="inline-flex items-center gap-2">
				<LoaderCircle className="size-4 animate-spin" />
				{label}
			</span>
		</div>
	);
}

function LoadError({ error }: { error: Error }) {
	return (
		<div className="rounded-3xl border border-red-200 bg-white p-8 text-center">
			<CircleAlert className="mx-auto size-8 text-red-600" />
			<p className="mt-3 font-semibold text-stone-900">Não foi possível abrir o acervo</p>
			<p className="mt-1 text-sm text-stone-500">{error.message}</p>
		</div>
	);
}
